/*
 * Copyright (C) 2026. to Present Applause App Quality, Inc. All rights reserved.
 */

import LogRocket from 'logrocket';

// LogRocket's `IRequest`/`IResponse`/`Nullable<T>` types live in an internal, non-exported
// `LR` namespace, so they're derived here from the public `init()` signature instead.
type LogRocketNetworkOptions = NonNullable<NonNullable<Parameters<typeof LogRocket.init>[1]>['network']>;
type LogRocketRequestSanitizer = NonNullable<LogRocketNetworkOptions['requestSanitizer']>;
type LogRocketResponseSanitizer = NonNullable<LogRocketNetworkOptions['responseSanitizer']>;
type LogRocketRequest = Parameters<LogRocketRequestSanitizer>[0];
type LogRocketHeaders = LogRocketRequest['headers'];

const REDACTED = '[redacted]';

// Header names (lowercase) that can carry auth/session material, so we'll err on the safe
// side and redact them from request/response logs.
const SENSITIVE_HEADER_NAMES = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-auth-token',
  'appl-refresh-tkn',
  'x-appl-refresh-tkn',
  'x-csrf-token',
  'x-xsrf-token',
]);

function redactHeaders(headers: LogRocketHeaders | undefined): LogRocketHeaders | undefined {
  if (!headers) {
    return headers;
  }
  return Object.fromEntries(
    Object.keys(headers).map((key) => [key, SENSITIVE_HEADER_NAMES.has(key.toLowerCase()) ? REDACTED : headers[key]]),
  ) as LogRocketHeaders;
}

// To start, while we trial LogRocket, we'll redact all fields
// in request/response bodies. If we find that this is too aggressive,
// we can implement a more nuanced approach later.
function redactFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactFields);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value as Record<string, unknown>).map((key) => [key, REDACTED]));
  }
  return REDACTED;
}

// Bodies are JSON strings (or undefined). Non-JSON bodies (e.g. multipart form
// data, plain text) can't be field-inspected, so they're redacted outright
// rather than risk passing through unsanitized PII.
function redactBody(body: string | undefined): string | undefined {
  if (body == null || body === '') {
    return body;
  }
  try {
    const parsed = JSON.parse(body);
    return JSON.stringify(redactFields(parsed));
  } catch {
    return REDACTED;
  }
}

export const logRocketRequestSanitizer: LogRocketRequestSanitizer = (request) => ({
  ...request,
  headers: redactHeaders(request.headers),
  body: redactBody(request.body),
});

export const logRocketResponseSanitizer: LogRocketResponseSanitizer = (response) => ({
  ...response,
  headers: redactHeaders(response.headers),
  body: redactBody(response.body),
});
